/**
 * DidYouKnowFact — rotating food trivia for menu-scan loading state.
 *
 * Extracted from the dead AnalyzingView in pages/Index.tsx so the active
 * collecting view can use it during first-page processing. 73 facts, randomized
 * starting index per mount, fade transition every 7s.
 *
 * Renders a self-contained vertical stack: emoji hero, "did you know?" eyebrow,
 * fact body, and an optional progress dots row. The caller is responsible for
 * the surrounding card and action buttons.
 */

import { useEffect, useState } from "react";

const FOOD_FACTS: [string, string][] = [
  ["🍕", "The world's most expensive pizza costs $12,000 and takes 72 hours to make"],
  ["🥑", "Avocados are technically berries. Strawberries aren't."],
  ["🍝", "Italy has over 600 pasta shapes. Most restaurants use about 10."],
  ["🌶️", "Capsaicin tricks your brain into thinking you're literally on fire"],
  ["🍣", "Sushi rice was originally used to ferment fish, not eat"],
  ["👨‍🍳", "French chefs learn 5 'mother sauces' — every other sauce comes from these"],
  ["🥐", "Croissants were invented in Austria, not France"],
  ["🍫", "White chocolate isn't technically chocolate — zero cocoa solids"],
  ["🥩", "Wagyu cattle in Japan get daily massages and listen to classical music"],
  ["🍋", "Lemons float but limes sink. It's a density thing."],
  ["🌮", "Tacos al pastor was inspired by Lebanese shawarma brought to Mexico"],
  ["🌺", "Vanilla is the world's most popular flavor but the second most expensive spice"],
  ["🧅", "Onions make you cry because they release a gas that forms acid in your tears"],
  ["🍔", "The average American eats 50 billion burgers a year. 3 per person per week."],
  ["🥟", "Dumplings exist in almost every cuisine — pierogi, gyoza, empanadas, momo"],
  ["🍰", "Tiramisu literally means 'pick me up' in Italian"],
  ["🫒", "Olives straight from the tree taste absolutely terrible. Months of curing needed."],
  ["🍜", "Ramen was invented in China but perfected in Japan"],
  ["🧀", "A wheel of Parmigiano-Reggiano takes 2 years and 131 gallons of milk"],
  ["🌽", "Corn is technically a grass. Your salad is basically lawn clippings."],
  ["🥖", "French law says bread can only be flour, water, salt, and yeast"],
  ["🐔", "Chickens outnumber humans 3 to 1 on Earth"],
  ["🍾", "Champagne has about 49 million bubbles per bottle"],
  ["🥭", "Mangoes are related to cashews and poison ivy. Wild."],
  ["🍩", "Donuts have holes because a sea captain thought the center was always raw"],
  ["☕", "Coffee beans aren't beans — they're pits of a cherry-like fruit"],
  ["🐙", "Octopus can solve mazes. You're eating a genius."],
  ["🌿", "Basil means 'king' in Greek. Royalty for your pasta."],
  ["🥚", "Egg shell color depends on the chicken's earlobes. Seriously."],
  ["🍿", "Popcorn can pop up to 3 feet in the air"],
  ["🥕", "Carrots were originally purple. Orange ones were bred in the Netherlands."],
  ["🍪", "The chocolate chip cookie was invented by accident in 1938"],
  ["🍯", "Honey never spoils. 3000-year-old honey from Egyptian tombs — still edible."],
  ["🍌", "Bananas are radioactive. You'd need 10 million at once for it to matter."],
  ["🥥", "Coconut water can be used as emergency IV fluid"],
  ["🫑", "Bell peppers change color as they ripen — green → yellow → orange → red"],
  ["👨‍🍳", "A chef's hat traditionally has 100 pleats — one for each way to cook an egg"],
  ["🦞", "Lobster used to be prison food. The cockroach of the sea."],
  ["🥜", "Peanuts aren't nuts — they're legumes that grow underground"],
  ["🥗", "Caesar salad was invented in Mexico by an Italian immigrant, not in Rome"],
  ["🐝", "It takes 12 bees their entire lifetime to make one teaspoon of honey"],
  ["🫕", "Fondue is Switzerland's national dish — promoted to boost cheese sales"],
  ["🍵", "Tea is the most consumed beverage in the world after water"],
  ["🧆", "McDonald's sells McFalafel in the Middle East"],
  ["🍅", "Ketchup was sold as medicine in the 1830s"],
  ["🍍", "Pineapples take 2-3 years to grow. Each plant makes just one."],
  ["🫚", "Nutmeg is a hallucinogen in large doses. Don't try it."],
  ["🧀", "The most stolen food in the world is cheese"],
  ["🍦", "Astronaut ice cream was never actually eaten in space"],
  ["🥫", "Spam is short for 'spiced ham' and is huge in Hawaii and South Korea"],
  ["🫘", "Coffee was discovered by Ethiopian goats who got hyper eating the berries"],
  ["🦑", "Squid ink pasta was originally a fisherman's meal — waste nothing"],
  ["🍇", "It takes about 2.5 pounds of grapes to make one bottle of wine"],
  ["🧈", "Butter was used as currency in medieval Norway"],
  ["🥒", "Cucumbers are 96% water. You're basically eating a crunchy drink."],
  ["🐄", "A cow gives nearly 200,000 glasses of milk in her lifetime"],
  ["🍑", "Peaches and almonds are in the same family. Cousins."],
  ["🫛", "Wasabi is so hard to grow that most restaurants serve dyed horseradish"],
  ["🦐", "Shrimp hearts are in their heads. Romantic."],
  ["🍊", "The color orange was named after the fruit, not the other way around"],
  ["🧊", "Ice cream headaches happen because cold hits a nerve cluster on the roof of your mouth"],
  ["🥬", "Lettuce is a member of the sunflower family"],
  ["🫧", "Sparkling water was invented by Joseph Priestley — who also discovered oxygen"],
  ["🐟", "Swordfish and marlin are the fastest fish — 68 mph. Your sushi was an athlete."],
  ["🍐", "Pears ripen from the inside out. By the time the outside is soft, the core is mush."],
  ["🥞", "Pancakes are one of the oldest foods — ancient Greeks ate them with honey"],
  ["🫐", "Blueberries are one of the only naturally blue foods in nature"],
  ["🌰", "Cashews grow on the bottom of a fruit called a cashew apple"],
  ["🍗", "KFC is a Christmas tradition in Japan since 1974"],
  ["🧄", "Garlic can be used as an adhesive. Vampires AND glue."],
  ["🥝", "Kiwis have more vitamin C than oranges, gram for gram"],
  ["🐖", "Pigs have 15,000 taste buds. Humans have about 10,000."],
  ["🫔", "Tamales date back to 8000 BC — older than the wheel"],
  ["🍄", "Mushrooms are genetically closer to humans than to plants"],
  ["🥄", "The fork wasn't common in Europe until the 18th century. Fingers first."],
  ["🍞", "Sourdough is the oldest form of leavened bread — over 5,000 years old"],
  ["🦃", "Turkeys can blush. Their heads change color with their mood."],
  ["🍫", "It takes about 400 cocoa beans to make one pound of chocolate"],
  ["🫙", "Pickling food predates refrigeration by thousands of years"],
  ["🌶️", "The Carolina Reaper is so hot it's measured in the millions of Scoville units"],
  ["🍤", "Shrimp can only swim backwards"],
  ["🧊", "The first ice cream cone was rolled at the 1904 World's Fair when a vendor ran out of cups"],
  ["🥓", "The smell of bacon comes from over 150 distinct chemical compounds"],
  ["🍠", "Sweet potatoes and regular potatoes aren't related at all"],
  ["🫓", "Naan is traditionally baked stuck to the wall of a clay tandoor oven"],
  ["🍮", "Crème brûlée means 'burnt cream' — the crackly top is caramelized sugar"],
  ["🐐", "Goat is the most widely eaten meat in the world"],
  ["🥘", "Paella was originally a farmer's lunch cooked over an open fire in the fields"],
  ["🍢", "Umami, the fifth taste, wasn't scientifically recognized until 1908"],
  ["🫖", "Earl Grey tea gets its flavor from bergamot, a type of citrus"],
  ["🥯", "Bagels are boiled before they're baked — that's what makes them chewy"],
  ["🍈", "A Japanese Yubari melon once sold for over $27,000 at auction"],
  ["🌭", "Hot dogs and the buns they go in are often sold in mismatched quantities on purpose… nobody knows why"],
];

interface DidYouKnowFactProps {
  /** Milliseconds between fact rotations. Default 7000. */
  rotateMs?: number;
  /** Show the secondary "did you know?" eyebrow label. Default true. */
  showEyebrow?: boolean;
  /** Tailwind classes appended to the outer wrapper. */
  className?: string;
  /** When true, stop cycling and freeze on the current fact. */
  paused?: boolean;
}

export function DidYouKnowFact({
  rotateMs = 7000,
  showEyebrow = true,
  className = "",
  paused = false,
}: DidYouKnowFactProps) {
  const [factIndex, setFactIndex] = useState(() =>
    Math.floor(Math.random() * FOOD_FACTS.length),
  );
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (paused) return;          // frozen on current fact
    const timer = setInterval(() => {
      // Fade out, swap fact at mid-fade, fade in
      setVisible(false);
      setTimeout(() => {
        setFactIndex((prev) => (prev + 1) % FOOD_FACTS.length);
        setVisible(true);
      }, 380);
    }, rotateMs);
    return () => clearInterval(timer);
  }, [rotateMs, paused]);

  const [emoji, fact] = FOOD_FACTS[factIndex % FOOD_FACTS.length];

  return (
    <div className={`flex flex-col items-center text-center ${className}`}>
      {/* Animated emoji hero with concentric ring decoration */}
      <div className="relative mb-5">
        <div className="absolute inset-[-18px] rounded-full border border-primary/10 analyzing-ring" />
        <div
          className="absolute inset-[-9px] rounded-full border border-primary/15 analyzing-ring"
          style={{ animationDelay: "0.5s" }}
        />
        <div
          className={`text-7xl transition-all duration-500 ${
            visible ? "scale-100 opacity-100" : "scale-75 opacity-0"
          }`}
          style={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.1))" }}
        >
          {emoji}
        </div>
      </div>

      {showEyebrow && (
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 mb-2">
          did you know?
        </p>
      )}

      <div className="w-full max-w-xs min-h-[72px] flex items-center justify-center">
        <p
          className={`text-base font-semibold text-foreground leading-snug px-2 transition-all duration-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
          }`}
        >
          {fact}
        </p>
      </div>
    </div>
  );
}

export default DidYouKnowFact;
